import {
  FlexBox,
  Modal,
  ModalFoot,
  ModalHead,
  Page,
  TabsWithContent,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { PureComponent } from 'react';

import { GlobalCsatSwitch } from '../../common/GlobalCsatSwitch';
import { Csat } from '../../common/types';
import { EmailAccountSetting } from '../EmailPanel/EmailPanel';

import { EmailAccountsList } from './components/EmailAccountsList';
import { EmailCSATSettingsForm } from './components/EmailCSATSettingsForm';
import { FormValues } from './components/EmailCSATSettingsForm/EmailCSATSettingsForm';
import { LanguageCustomizationList } from './components/LanguageCustomizationList';
import styles from './index.module.scss';
import { EmailCSAT } from './types';

interface Props {
  className?: string;
  existingEmails: EmailAccountSetting[];
  languages: EmailCSAT.Language[];
  onLanguageEdit: (language: EmailCSAT.Language) => void;
  onSettingsSave: (values: FormValues) => void;
  onAccountStatusChange: (change: EmailCSAT.EmailAccountStatusChange) => void;
  onLanguageStatusChange: (
    change: EmailCSAT.LanguageCustomizationStatusChange,
  ) => void;
  onGlobalStatusChange: (change: Csat.CsatStatusChange) => void;
  isLoadingEmailsList?: boolean;
  globalSettingsInitialValue?: FormValues;
  isLoadingGlobalSettings: boolean;
  isEmailCsatActive: boolean;
  isEmailCsatSetup: boolean;
  defaultLanguage?: string;
  initiallyActiveTab: EmailCsatSettingTabs;
}

interface State {
  confirmationModal: {
    isOpen: boolean;
    context: EmailCSAT.EmailAccountStatusChange | null;
  };
}

export enum EmailCsatSettingTabs {
  Accounts = 'Accounts',
  Customizations = 'Language customizations',
}

export class EmailPage extends PureComponent<Props, State> {
  static defaultProps = {
    initiallyActiveTab: EmailCsatSettingTabs.Accounts,
  };

  constructor(props: Props) {
    super(props);
    this.state = { confirmationModal: { isOpen: false, context: null } };
  }

  @boundMethod
  handleEmailAccountStatusChange(change: EmailCSAT.EmailAccountStatusChange) {
    if (this.statusHasChangedToDeActive(change)) {
      this.openConfirmationModal(change);
    } else {
      this.props.onAccountStatusChange(change);
    }
  }

  @boundMethod
  handleConfirmedAccountStatusChange() {
    const { confirmationModal } = this.state;
    if (confirmationModal.context) {
      this.props.onAccountStatusChange(confirmationModal.context);
    }
    this.closeConfirmationModal();
  }

  openConfirmationModal(context: EmailCSAT.EmailAccountStatusChange) {
    this.setState({ confirmationModal: { isOpen: true, context: context } });
  }

  @boundMethod
  closeConfirmationModal() {
    this.setState({ confirmationModal: { isOpen: false, context: null } });
  }

  statusHasChangedToDeActive(
    change: EmailCSAT.EmailAccountStatusChange,
  ): boolean {
    return change.newStatus === false;
  }

  render() {
    const { existingEmails } = this.props;

    const emailAddresses = existingEmails.map(({ address }) => address);

    const tabsDefinition = Object.values(EmailCsatSettingTabs).map(
      (tabName) => ({
        id: tabName,
        label: tabName,
      }),
    );

    const notAllowedToBeDisabledLanguages: string[] = [];

    if (this.props.defaultLanguage) {
      notAllowedToBeDisabledLanguages.push(this.props.defaultLanguage);
    }

    return (
      <>
        <Page.Section
          callout={
            <GlobalCsatSwitch
              channel="email"
              onChange={this.props.onGlobalStatusChange}
              isActive={this.props.isEmailCsatActive}
            />
          }
        >
          <EmailCSATSettingsForm
            enableReinitialize={true}
            initialValues={this.props.globalSettingsInitialValue}
            existingEmails={emailAddresses}
            onSubmit={this.props.onSettingsSave}
            isLoading={this.props.isLoadingGlobalSettings}
            disabled={!this.props.isEmailCsatSetup}
          />

          <TabsWithContent
            className={styles.tabsWrapper}
            initiallySelectedTabId={this.props.initiallyActiveTab}
            tabs={tabsDefinition}
          >
            <EmailAccountsList
              accounts={existingEmails}
              onStatusChange={this.handleEmailAccountStatusChange}
              isLoading={this.props.isLoadingEmailsList}
              disabled={!this.props.isEmailCsatSetup}
            />

            {existingEmails.length > 0 ? (
              <LanguageCustomizationList
                languages={this.props.languages}
                onEdit={this.props.onLanguageEdit}
                onLanguageStatusChange={this.props.onLanguageStatusChange}
                disabled={!this.props.isEmailCsatSetup}
                notAllowedToBeDisabledLanguages={
                  notAllowedToBeDisabledLanguages
                }
              />
            ) : (
              <FlexBox justifyContent="center" alignItems="center">
                No active emails found!
              </FlexBox>
            )}
          </TabsWithContent>
        </Page.Section>

        <Modal
          isOpen={this.state.confirmationModal.isOpen}
          onClose={this.closeConfirmationModal}
        >
          <ModalHead title="Are you sure you want to disable CSAT for this email account?">
            This will not trigger a CSAT when conversations to this email are
            closed.
          </ModalHead>
          <ModalFoot
            confirmOnClick={this.handleConfirmedAccountStatusChange}
            cancelOnClick={this.closeConfirmationModal}
            confirmName="Disable"
          />
        </Modal>
      </>
    );
  }
}
